import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { fetchCategories } from "../../api/categoryService";
import {
  fetchLeaderboards,
  fetchTeamLeaderboards,
} from "../../api/leaderboardService";

const TABS = [
  { id: "general", label: "Général" },
  { id: "category", label: "Catégorie" },
  { id: "daily", label: "Quotidien" },
  { id: "monthly", label: "Mensuel" },
  { id: "team_general", label: "Teams" },
  { id: "team_category", label: "Teams catégorie" },
  { id: "likes", label: "Likes" },
];

function EmptyState({ children }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-center text-sm text-slate-500">
      {children}
    </div>
  );
}

function SearchInput({ value, onChange }) {
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Rechercher un pseudo..."
        className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-9 pr-4 text-sm outline-none focus:border-sky-400 focus:bg-white sm:w-64"
      />
    </div>
  );
}

function LeaderboardRows({ rows, search }) {
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (r.pseudo || "").toLowerCase().includes(q));
  }, [rows, search]);

  if (!rows?.length) {
    return <EmptyState>Aucun score comptabilisé pour ce classement.</EmptyState>;
  }
  if (!filtered.length) {
    return <EmptyState>Aucun joueur ne correspond à « {search} ».</EmptyState>;
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
      {filtered.map((row) => {
        const isRegistered = /^\d+$/.test(String(row.player_id || ""));
        const pseudo = row.pseudo || "Joueur";
        return (
          <div
            key={`${row.player_id}-${row.position}`}
            className="grid grid-cols-[56px_minmax(0,1fr)_100px] items-center gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white">
              #{row.position}
            </div>
            <div className="flex min-w-0 items-center gap-3">
              {row.avatar_id ? (
                <img
                  src={row.avatar_id}
                  alt={pseudo}
                  className="h-12 w-12 shrink-0 rounded-2xl object-cover border border-slate-200"
                />
              ) : (
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-pixy-green to-pixy-blue text-base font-black text-white">
                  {pseudo.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {isRegistered ? (
                    <Link
                      to={`/joueur/u/${row.player_id}`}
                      className="truncate font-bold text-slate-950 hover:text-pixy-blue hover:underline"
                    >
                      {pseudo}
                    </Link>
                  ) : (
                    <span className="truncate font-bold text-slate-950">{pseudo}</span>
                  )}
                  {row.team?.tag && (
                    row.team?.id ? (
                      <Link
                        to={`/equipe/t/${row.team.id}`}
                        className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 hover:bg-amber-200"
                      >
                        [{row.team.tag}]
                      </Link>
                    ) : (
                      <span className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                        [{row.team.tag}]
                      </span>
                    )
                  )}
                </div>
                <div className="text-xs text-slate-500">
                  {row.games_played || 0} partie{row.games_played > 1 ? "s" : ""}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-[0.16em] text-slate-400">
                Points
              </div>
              <div className="font-black text-sky-700">{row.score || 0}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TeamLeaderboardRows({ rows, search }) {
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.tag || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  if (!rows?.length) {
    return <EmptyState>Aucune team comptabilisée pour ce classement.</EmptyState>;
  }
  if (!filtered.length) {
    return <EmptyState>Aucune team ne correspond à « {search} ».</EmptyState>;
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
      {filtered.map((row) => (
        <div
          key={`${row.team_id}-${row.position}`}
          className="grid grid-cols-[56px_minmax(0,1fr)_88px] items-center gap-3 border-b border-slate-100 px-4 py-4 last:border-b-0"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white">
            #{row.position}
          </div>
          <div className="flex min-w-0 items-center gap-3">
            {row.logo ? (
              <img
                src={row.logo}
                alt=""
                className="h-11 w-11 rounded-2xl object-cover"
              />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-500 text-xs font-black text-white">
                {row.tag}
              </div>
            )}
            <div className="min-w-0">
              <Link
                to={`/equipe/t/${row.team_id}`}
                className="truncate font-bold text-slate-950 hover:underline"
              >
                [{row.tag}] {row.name}
              </Link>
              <div className="text-xs text-slate-500">
                {row.games_played || 0} partie{row.games_played > 1 ? "s" : ""} team
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.16em] text-slate-400">
              Points
            </div>
            <div className="font-black text-amber-700">{row.score || 0}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LikesLeaderboard({ leaderboard, search }) {
  const profileRows = leaderboard?.results || [];
  const imageRows = leaderboard?.image_results || [];

  const filteredProfiles = useMemo(() => {
    if (!search.trim()) return profileRows;
    const q = search.trim().toLowerCase();
    return profileRows.filter((r) => (r.pseudo || "").toLowerCase().includes(q));
  }, [profileRows, search]);

  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <div>
        <h3 className="mb-3 text-sm font-black uppercase tracking-[0.18em] text-slate-500">
          Profils les plus likés
        </h3>
        {!filteredProfiles.length ? (
          <EmptyState>
            {search.trim() ? `Aucun profil ne correspond à « ${search} ».` : "Aucun profil like pour le moment."}
          </EmptyState>
        ) : (
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
            {filteredProfiles.map((row) => (
              <div
                key={`${row.user_id}-${row.position}`}
                className="grid grid-cols-[56px_minmax(0,1fr)_88px] items-center gap-3 border-b border-slate-100 px-4 py-4 last:border-b-0"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-pink-600 text-sm font-black text-white">
                  #{row.position}
                </div>
                <Link
                  to={`/joueur/u/${row.user_id}`}
                  className="truncate font-bold text-slate-950 hover:underline"
                >
                  {row.pseudo || "Joueur"}
                </Link>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-400">
                    Likes
                  </div>
                  <div className="font-black text-pink-700">{row.likes_count || row.score || 0}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="mb-3 text-sm font-black uppercase tracking-[0.18em] text-slate-500">
          Images les plus likées
        </h3>
        {!imageRows.length ? (
          <EmptyState>Aucune image likee pour le moment.</EmptyState>
        ) : (
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
            {imageRows.map((row) => (
              <div
                key={`${row.image_id}-${row.position}`}
                className="grid grid-cols-[56px_minmax(0,1fr)_88px] items-center gap-3 border-b border-slate-100 px-4 py-4 last:border-b-0"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-950 text-sm font-black text-white">
                  #{row.position}
                </div>
                <div className="min-w-0">
                  <div className="truncate font-bold text-slate-950">
                    {row.answer || "Image"}
                  </div>
                  <div className="text-xs text-slate-500">{row.category}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-[0.16em] text-slate-400">
                    Likes
                  </div>
                  <div className="font-black text-pink-700">{row.likes_count || 0}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function initialTab() {
  const queryTab = new URLSearchParams(window.location.search).get("tab");
  return TABS.some((tab) => tab.id === queryTab) ? queryTab : "general";
}

export default function Classement() {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [leaderboard, setLeaderboard] = useState({ results: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const isTeamTab = activeTab.startsWith("team_");
  const needsCategoryFilter = ["category", "daily", "monthly", "team_category"].includes(activeTab);

  useEffect(() => {
    const controller = new AbortController();

    fetchCategories(controller.signal)
      .then((items) => {
        const activeItems = Array.isArray(items) ? items : [];
        setCategories(activeItems);
        if (!selectedCategory && activeItems[0]?.slug) {
          setSelectedCategory(activeItems[0].slug);
        }
      })
      .catch(() => setCategories([]));

    return () => controller.abort();
  }, [selectedCategory]);

  useEffect(() => {
    const controller = new AbortController();

    const loadLeaderboard = async () => {
      setIsLoading(true);
      setError("");

      try {
        const normalizedType = isTeamTab
          ? activeTab.replace("team_", "")
          : activeTab;
        const loader = isTeamTab ? fetchTeamLeaderboards : fetchLeaderboards;
        const data = await loader(
          {
            type: normalizedType,
            category: needsCategoryFilter ? selectedCategory : undefined,
          },
          controller.signal
        );
        setLeaderboard(data);
      } catch (loadError) {
        setError("Impossible de charger ce classement pour le moment.");
        setLeaderboard({ results: [] });
      } finally {
        setIsLoading(false);
      }
    };

    if (!needsCategoryFilter || selectedCategory) {
      loadLeaderboard();
    }

    return () => controller.abort();
  }, [activeTab, selectedCategory, isTeamTab, needsCategoryFilter]);

  const activeLabel = useMemo(
    () => TABS.find((tab) => tab.id === activeTab)?.label || "Classement",
    [activeTab]
  );

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearch("");
  };

  return (
    <div className="bg-slate-50">
      <div className="mx-auto grid min-h-[calc(100vh-160px)] max-w-screen-2xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)] lg:px-8">
        <aside className="h-fit rounded-[28px] border border-slate-200 bg-white p-3 shadow-sm">
          <div className="px-3 py-3">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-600">
              PixyQuiz
            </div>
            <h1 className="mt-2 text-2xl font-black text-slate-950">
              Classements
            </h1>
          </div>
          <nav className="mt-2 grid gap-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={`rounded-2xl px-4 py-3 text-left text-sm font-semibold transition ${
                  activeTab === tab.id
                    ? "bg-slate-950 text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-950"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 rounded-[32px] border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
          <div className="flex flex-col gap-4 border-b border-slate-200 pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-400">
                Classement public
              </div>
              <h2 className="mt-2 text-3xl font-black text-slate-950">
                {activeLabel}
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                {activeTab === "likes"
                  ? "Données issues des likes persistants sur profils et images."
                  : isTeamTab
                  ? "Données issues des points team historisés après les parties."
                  : "Données issues des parties terminées et comptabilisées."}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {needsCategoryFilter ? (
                <select
                  value={selectedCategory}
                  onChange={(event) => setSelectedCategory(event.target.value)}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-800 outline-none focus:border-sky-400"
                >
                  {categories.map((category) => (
                    <option key={category.slug} value={category.slug}>
                      {category.name}
                    </option>
                  ))}
                </select>
              ) : null}
              {activeTab !== "likes" && (
                <SearchInput value={search} onChange={setSearch} />
              )}
            </div>
          </div>

          {activeTab === "likes" && (
            <div className="mt-4">
              <SearchInput value={search} onChange={setSearch} />
            </div>
          )}

          <div className="mt-6">
            {error ? (
              <EmptyState>{error}</EmptyState>
            ) : isLoading ? (
              <div className="grid gap-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-20 animate-pulse rounded-3xl bg-slate-100"
                  />
                ))}
              </div>
            ) : activeTab === "likes" ? (
              <LikesLeaderboard leaderboard={leaderboard} search={search} />
            ) : isTeamTab ? (
              <TeamLeaderboardRows rows={leaderboard.results} search={search} />
            ) : (
              <LeaderboardRows rows={leaderboard.results} search={search} />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
